import { motion } from "framer-motion";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";

import { AllowedFileType, FILE_EXTENSION_ICON_MAP, FILE_VALIDATION_CONFIGS, getFileExtension, validateFile } from "@/utils/helper/fileUploadValidation";
import axios from "axios";
import { getUploadUrls } from "@/services/common/docs.services";
import { toastErrorMessage } from "@/utils/helper/myToast";
import { Center } from "../css";
import { Paperclip, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import Icon from "../ui/Image";
import { InfoText, LightText, LightText2 } from "../ui/Text";
import { formatLengthyName } from "@/utils/helper/string";
import CustomLink from "../ui/CustomLink";
import { FormUIContext } from "@/context";
import { getFileSize } from "@/components/employee/modal/AddAttachments";

interface UploadDocumentsInputProps {
  label: string;
  field: string;
  defaultValue?: string;
  previewInfo?: any;
  placeholder?: string;
  disabled?: boolean;
  uploadKey: string;
  validation: {
    required: boolean;
    allowedFileType: AllowedFileType;
    maxFileSize?: number;
  };
  maxFiles?: number;
  wigetInfo?: { btnText: string; variant: any };
}

const UploadDocumentsInput: React.FC<UploadDocumentsInputProps> = ({
  field,
  disabled = false,
  validation,
  uploadKey,
  defaultValue,
  maxFiles = 1,
  previewInfo,
  wigetInfo,
}) => {
  const {
    register,
    setError,
    setValue,
    clearErrors,
    watch,
    formState: { errors },
  } = useFormContext();
  const { setIsFormLoading }: any = useContext(FormUIContext) || {};
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileContainerRef = useRef<HTMLDivElement>(null);

  const fieldValue = watch(field);
  const isUploading = uploadProgress > 0 && uploadProgress < 100;

  const errorMessage: any = errors[field]?.message;
  const previewFileExtension: any = getFileExtension(previewInfo?.path||previewInfo?.url);
  const selectedFileExtension: any = getFileExtension(selectedFile?.name || "");

  const handleUploadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    if (errorMessage) clearErrors(field);
    // Check: no. of files
    if (files.length > maxFiles) {
      setError(field, { message: `Maximum ${maxFiles} file${maxFiles > 1 ? "s" : ""} allowed` });
      return;
    }
    // validate all files
    for (const file of files) {
      const validationError = validateFile(file, validation.allowedFileType);
      if (validationError) {
        setError(field, { message: validationError });
        return;
      }
    }
    setSelectedFile(files[0]);
    if (setIsFormLoading) {
      setIsFormLoading(true);
    }
    setUploadProgress(1);

    for (const file of files) {
      try {
        const response: any = await getUploadUrls({
          fileName: file.name,
          fileType: file.type,
          uploadKey,
        });

        const { preSignedUrl, objectKey } = response.data?.data || {};

        await axios.put(preSignedUrl, file, {
          headers: {
            "Content-Type": file.type,
          },
          onUploadProgress: (progressEvent: any) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total) - 1;
            setUploadProgress(percentCompleted);
          },
        });
        // toastSuccessMessage("File uploaded successfully!");
        const fieldValue =
          field === "supportingDoc" ? { path: objectKey, name: file?.name, type: file?.type, size: getFileSize(file) } : { path: objectKey, name: file?.name };
        setValue(field, fieldValue, { shouldDirty: true, shouldTouch: true });
      } catch (error) {
        console.error("Error uploading file:", error);
        toastErrorMessage("Failed to upload file.");
      } finally {
        setUploadProgress(100);
      }
    }
    if (setIsFormLoading) {
      setIsFormLoading(false);
    }
  };

  const getRegisterOptions = () => {
    if (disabled) return {};
    return {
      required: validation?.required ? (previewInfo ? false : `Please attach ${previewInfo ? "new" : ""} file.`) : false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        try {
          setValue(field, JSON.parse(e.target.value));
        } catch {
          setValue(field, null);
        }
      },
    };
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setValue(field, null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (!fieldValue) {
      setValue(field, defaultValue, { shouldDirty: false });
    }
  }, [defaultValue]);

  useEffect(() => {
    if ((selectedFile || errorMessage) && fileContainerRef?.current) {
      fileContainerRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedFile, errorMessage]);
// console.log({defaultValue})
  return (
    <div className="pb-1 ">
      <input
        ref={fileInputRef}
        type="file"
        id={`upload-file-${field}`}
        onChange={handleUploadFile}
        style={{ display: "none" }}
        multiple={maxFiles > 1}
        disabled={isUploading}
        accept={FILE_VALIDATION_CONFIGS[validation.allowedFileType as keyof typeof FILE_VALIDATION_CONFIGS].allowedTypes.join(",")}
      />
      <label className="cursor-pointer inline-block" htmlFor={`upload-file-${field}`}>
        <Button
          disabled={isUploading}
          asChild
          variant={wigetInfo?.variant || "default"}
          className={`${wigetInfo?.variant ? "text-text-primary !text-xs" : ""}`}
        >
          <Center>
            <Paperclip size={16} className={`mr-2 ${wigetInfo?.variant ? "text-text-primary" : "text-white"}`} /> {wigetInfo?.btnText || "Attach"}
          </Center>
        </Button>
      </label>
      <input type="hidden" {...register(field, getRegisterOptions())} />
      {errorMessage && <InfoText text={errorMessage} className="my-4" />}
      <div ref={fileContainerRef}>
        {selectedFile && (
          <div className="mt-4 p-3 bg-primaryOpacity-5 rounded flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon src={selectedFileExtension ? FILE_EXTENSION_ICON_MAP[selectedFileExtension] : ""} alt="" className="size-6" />
              <LightText2 text={formatLengthyName(selectedFile.name)} className="line-clamp-1" />
              {isUploading ? (
                <div className="px-0.5">
                  <motion.div
                    className="w-4 h-4 rounded-full"
                    style={{
                      background: "conic-gradient(from 0deg, #FDF7FF, #FDF7FF, #FDF7FF, #C59EF7, #9A6AD4, #6F36BC)",
                      maskImage: "radial-gradient(transparent 50%, black 50%)",
                      WebkitMaskImage: "radial-gradient(transparent 50%, black 50%)",
                    }}
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  />
                </div>
              ) : (
                <Icon src="/icons/verified.svg" alt="check-icon" />
              )}
            </div>
            <Center className="gap-x-1">
              <LightText text={(selectedFile.size / (1024 * 1024)).toFixed(2) + "MB"} />
              <button onClick={handleRemoveFile} className=" hover:scale-103">
                <Trash2 className=" text-text-default size-3 hover:scale-103" />
              </button>
            </Center>
          </div>
        )}
        {isUploading && (
          <div className="relative">
            <div className="w-full bg-gray-200 rounded-full h-0.5">
              <div className=" bg-text-primary/50 h-0.5 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        )}

        {/* Preview File for Update */}
        {!selectedFile && previewInfo && (
          <div className="mt-4 p-3 bg-primaryOpacity-5 rounded flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon src={previewFileExtension ? FILE_EXTENSION_ICON_MAP[previewFileExtension] : ""} alt="" className="size-6" />
              <LightText2 text={formatLengthyName(previewInfo?.name)} className="line-clamp-1" />
            </div>
            <CustomLink link={{ text: "View document", value: previewInfo?.path||previewInfo?.url }} isExternal className=" text-13" />
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadDocumentsInput;
